import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { SystemUser } from '@/types/user';
import systemAdminUsersApi from '@/api/modules/systemAdminUsers';

export interface SystemAdminUserFilters {
  search: string;
  accountId: string | null;
}

export const systemAdminUserSortFields = [
  'first_name',
  'last_name',
  'email',
  'is_system_admin',
  'inserted_at',
  'last_login_at',
] as const;

export type SystemAdminUserSortField = typeof systemAdminUserSortFields[number];
export type SystemAdminUserSortOrder = 'asc' | 'desc';

export const isSystemAdminUserSortField = (field: string): field is SystemAdminUserSortField => {
  return systemAdminUserSortFields.includes(field as SystemAdminUserSortField);
};

export const useSystemAdminUserStore = defineStore('system-admin-user', () => {
  const users = ref<SystemUser[]>([]);
  const accountRoles = ref<any[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const totalUsers = ref(0);
  const page = ref(1);
  const pageSize = ref(10);
  const sortField = ref<SystemAdminUserSortField>('inserted_at');
  const sortOrder = ref<SystemAdminUserSortOrder>('desc');
  const filters = ref<SystemAdminUserFilters>({
    search: '',
    accountId: null,
  });

  const fetchUsers = async (newPage: number = page.value, newPageSize: number = pageSize.value) => {
    isLoading.value = true;
    error.value = null;
    try {
      page.value = newPage;
      pageSize.value = newPageSize;
      const response = await systemAdminUsersApi.getUsers({
        page: page.value,
        page_size: pageSize.value,
        search: filters.value.search.trim() || undefined,
        account_id: filters.value.accountId || undefined,
        sort_by: sortField.value,
        sort_order: sortOrder.value,
      });
      users.value = response.items;
      totalUsers.value = response.total;
    } catch (e: any) {
      error.value = e.message;
    } finally {
      isLoading.value = false;
    }
  };

  const setSort = async (field: SystemAdminUserSortField, order: SystemAdminUserSortOrder) => {
    sortField.value = field;
    sortOrder.value = order;
    await fetchUsers(1, pageSize.value);
  };

  const setFilters = async (nextFilters: SystemAdminUserFilters) => {
    filters.value = {
      search: nextFilters.search,
      accountId: nextFilters.accountId,
    };
    await fetchUsers(1, pageSize.value);
  };

  const clearFilters = async () => {
    await setFilters({
      search: '',
      accountId: null,
    });
  };

  const fetchAccountRoles = async (accountId: string) => {
    try {
      accountRoles.value = await systemAdminUsersApi.getRoles(accountId);
    } catch (e: any) {
      console.error('Failed to fetch account roles:', e);
    }
  };

  const createUser = async (data: any) => {
    isLoading.value = true;
    error.value = null;
    try {
      await systemAdminUsersApi.createUser(data);
      await fetchUsers(1, pageSize.value);
    } catch (e: any) {
      error.value = e.message;
      throw e;
    } finally {
      isLoading.value = false;
    }
  };

  const bulkUpdateUser = async (userId: string, data: any) => {
    isLoading.value = true;
    error.value = null;
    try {
      await systemAdminUsersApi.bulkUpdateUser(userId, data);
      await fetchUsers();
    } catch (e: any) {
      error.value = e.message;
      throw e;
    } finally {
      isLoading.value = false;
    }
  };

  const deleteUser = async (userId: string) => {
    isLoading.value = true;
    error.value = null;
    try {
      const shouldMoveToPreviousPage = users.value.length === 1 && page.value > 1;
      await systemAdminUsersApi.deleteUser(userId);
      await fetchUsers(shouldMoveToPreviousPage ? page.value - 1 : page.value, pageSize.value);
    } catch (e: any) {
      error.value = e.message;
      throw e;
    } finally {
      isLoading.value = false;
    }
  };

  const adminChangePassword = async (userId: string, newPassword: string) => {
    isLoading.value = true;
    error.value = null;
    try {
      await systemAdminUsersApi.changePassword(userId, newPassword);
    } catch (e: any) {
      error.value = e.message;
      throw e;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    users,
    accountRoles,
    isLoading,
    error,
    totalUsers,
    page,
    pageSize,
    sortField,
    sortOrder,
    filters,
    fetchUsers,
    setSort,
    setFilters,
    clearFilters,
    fetchAccountRoles,
    createUser,
    bulkUpdateUser,
    deleteUser,
    adminChangePassword,
  };
});
